/**
 * BoardNotesPanel - Right sidebar panel for board notes
 */

import { useEffect, useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { StickyNote, Plus, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBoardPanelsStore } from '@stores/boardPanelsStore';
import { toast } from '@components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import CollapsiblePanel from './CollapsiblePanel';
import BoardNoteCard from './BoardNoteCard';
import BoardNoteEditor from './BoardNoteEditor';

interface BoardNotesPanelProps {
  boardId: string;
}

export default function BoardNotesPanel({ boardId }: BoardNotesPanelProps) {
  const { t } = useTranslation();
  const {
    rightPanelOpen,
    toggleRightPanel,
    setRightPanelOpen,
    notes,
    selectedNoteId,
    isLoadingNotes,
    isSavingNote,
    notesError,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    selectNote,
  } = useBoardPanelsStore();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingNote, setDeletingNote] = useState<{ id: string; title: string } | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Load notes when panel opens
  useEffect(() => {
    if (rightPanelOpen && boardId) {
      void fetchNotes(boardId);
    }
  }, [rightPanelOpen, boardId, fetchNotes]);

  // Find selected note
  const selectedNote = useMemo(() => {
    if (!selectedNoteId) return null;
    return notes.find((n) => n.id === selectedNoteId) || null;
  }, [notes, selectedNoteId]);

  const handleCreateNote = async () => {
    setIsCreating(true);
    try {
      const note = await createNote(boardId, {
        title: t('board.noteTitlePlaceholder'),
        content: '',
      });
      selectNote(note.id);
      toast.success(t('board.noteCreated'));
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error(t('board.failedCreateNote'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateNote = async (data: { title?: string; content?: string }) => {
    if (!selectedNoteId) return;

    try {
      await updateNote(boardId, selectedNoteId, data);
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error(t('board.failedSaveNote'));
      throw error;
    }
  };

  const handleDeleteRequest = (noteId: string, title: string) => {
    setDeletingNote({ id: noteId, title });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingNote) return;

    try {
      await deleteNote(boardId, deletingNote.id);
      toast.success(t('board.noteDeleted'));
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error(t('board.failedDeleteNote'));
    } finally {
      setShowDeleteConfirm(false);
      setDeletingNote(null);
    }
  };

  const handleCloseEditor = () => {
    selectNote(null);
  };

  return (
    <>
      <CollapsiblePanel
        side="right"
        isOpen={rightPanelOpen}
        onToggle={toggleRightPanel}
        onClose={() => setRightPanelOpen(false)}
        title={t('board.notesTitle')}
        icon={StickyNote}
        actions={
          !selectedNote && (
            <button
              onClick={() => void handleCreateNote()}
              disabled={isCreating}
              className={clsx(
                'rounded p-1.5',
                'text-neutral-500 hover:text-neutral-700',
                'dark:text-neutral-400 dark:hover:text-neutral-200',
                'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'transition-colors'
              )}
              title={t('board.addNote')}
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          )
        }
      >
        {selectedNote ? (
          /* Note editor view */
          <BoardNoteEditor
            note={selectedNote}
            isSaving={isSavingNote}
            onSave={handleUpdateNote}
            onClose={handleCloseEditor}
          />
        ) : (
          /* Notes list view */
          <div className="flex h-full flex-col p-4">
            {/* Error state */}
            {notesError && (
              <div
                className={clsx(
                  'mb-4 flex items-center gap-2 rounded-lg p-3',
                  'bg-red-50 text-red-600',
                  'dark:bg-red-900/20 dark:text-red-400'
                )}
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm">{notesError}</span>
              </div>
            )}

            {/* Loading state */}
            {isLoadingNotes ? (
              <div className="flex flex-1 flex-col items-center justify-center text-neutral-500">
                <Loader2 className="mb-2 h-6 w-6 animate-spin" />
                <span className="text-sm">{t('board.loadingNotes')}</span>
              </div>
            ) : notes.length === 0 ? (
              /* Empty state */
              <button
                onClick={() => void handleCreateNote()}
                disabled={isCreating}
                className={clsx(
                  'flex flex-1 flex-col items-center justify-center rounded-lg',
                  'border-2 border-dashed border-neutral-300 dark:border-neutral-700',
                  'text-neutral-500 hover:border-neutral-400 hover:text-neutral-600',
                  'dark:text-neutral-400 dark:hover:border-neutral-600 dark:hover:text-neutral-300',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  'transition-colors'
                )}
              >
                {isCreating ? (
                  <Loader2 className="mb-2 h-8 w-8 animate-spin" />
                ) : (
                  <>
                    <StickyNote className="mb-2 h-8 w-8" />
                    <span className="text-sm font-medium">{t('board.noNotesYet')}</span>
                    <span className="text-xs">{t('board.clickToAddNote')}</span>
                  </>
                )}
              </button>
            ) : (
              /* Notes list */
              <div className="space-y-2">
                {notes.map((note) => (
                  <BoardNoteCard
                    key={note.id}
                    note={note}
                    isSelected={note.id === selectedNoteId}
                    onClick={() => selectNote(note.id)}
                    onDelete={() => handleDeleteRequest(note.id, note.title)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CollapsiblePanel>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingNote(null);
        }}
        onConfirm={() => void handleConfirmDelete()}
        title={t('board.deleteNoteTitle')}
        description={t('board.deleteNoteDescription', { title: deletingNote?.title ?? '' })}
        confirmLabel={t('common.delete')}
        variant="danger"
      />
    </>
  );
}
